import {
  getLanguageState,
  isLanguage,
  persistLanguage,
  readPreferredLanguage,
  SUPPORTED_LANGUAGES,
  updateLanguageState,
  type Language,
} from "./language";
import { installTranslationDomBridge } from "./domBridge";
import { resetGoogleTextSources } from "./googleText";

interface TranslateSdk {
  translate?: {
    TranslateElement?: new (options: object, container: string) => unknown;
  };
}

interface TranslateWidget {
  setEnabled?: (enabled: boolean) => void;
  showBanner?: (translate: boolean) => void;
}
declare global {
  interface Window {
    google?: TranslateSdk;
    planetwiseTranslateReady?: () => void;
  }
}

const SDK_ID = "planetwise-google-translate-sdk";
const CONTAINER_ID = "google_translate_element";
const TIMEOUT = 20_000;
let widgetPromise: Promise<HTMLSelectElement> | undefined;
let currentSelect: HTMLSelectElement | undefined;
let currentWidget: TranslateWidget | undefined;
let stopBridge: (() => void) | undefined;
let activeRequest: AbortController | undefined;
let clients = 0;
let cancelLoad: (() => void) | undefined;
let appliedLanguageObserver: MutationObserver | undefined;
let canRecoverAppliedLanguage = false;
const PROBE_SOURCE = "This page is ready to use.";
let translationProbe: HTMLSpanElement | undefined;

function resetTranslationProbe() {
  translationProbe?.remove();
  translationProbe = document.createElement("span");
  translationProbe.setAttribute("data-google-translate-probe", "");
  translationProbe.setAttribute("aria-hidden", "true");
  Object.assign(translationProbe.style, {
    position: "fixed",
    left: "0",
    top: "0",
    opacity: "0",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  });
  translationProbe.textContent = PROBE_SOURCE;
  document.body.appendChild(translationProbe);
}

function writeCookie(value: string): void {
  try {
    document.cookie = value;
  } catch {
    // Restricted browsers can reject cookie access. The live widget is the
    // source of truth; remembering its choice must never invalidate success.
  }
}

function cookiePaths(): string[] {
  const segments = location.pathname.split("/").filter(Boolean);
  const paths = new Set(["/"]);
  for (let i = 1; i <= segments.length; i++) {
    const path = `/${segments.slice(0, i).join("/")}`;
    paths.add(path);
    paths.add(`${path}/`);
  }
  return [...paths];
}

/** Remove only legacy Google translation cookies, including inherited scopes. */
export function clearTranslationCookies(): void {
  const domains: Array<string | undefined> = [undefined];
  const parts = location.hostname.split(".");
  for (let i = 0; i < parts.length - 1; i++)
    domains.push(parts.slice(i).join("."));
  for (const path of cookiePaths()) {
    for (const domain of domains) {
      writeCookie(
        `googtrans=; Max-Age=0; path=${path};${domain ? ` domain=${domain};` : ""} SameSite=Lax`,
      );
    }
  }
}

function rememberCookie(language: Language): void {
  clearTranslationCookies();
  if (language !== "en") {
    writeCookie(
      `googtrans=/en/${language}; path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`,
    );
  }
}

function getContainer(): HTMLElement {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement("div");
    container.id = CONTAINER_ID;
    document.body.appendChild(container);
  }
  container.classList.add("notranslate");
  container.setAttribute("translate", "no");
  container.setAttribute("aria-hidden", "true");
  return container;
}

function ensureWidget(): Promise<HTMLSelectElement> {
  stopBridge ??= installTranslationDomBridge();
  if (currentSelect?.isConnected) return Promise.resolve(currentSelect);
  if (widgetPromise) return widgetPromise;
  clearTranslationCookies();
  const container = getContainer();

  widgetPromise = new Promise<HTMLSelectElement>((resolve, reject) => {
    let initialized = false;
    let settled = false;
    let observer: MutationObserver | undefined;
    const timer = setTimeout(
      () =>
        fail(
          new Error("Google Translate took too long to load. Please retry."),
        ),
      TIMEOUT,
    );

    function clean() {
      clearTimeout(timer);
      observer?.disconnect();
      window.planetwiseTranslateReady = undefined;
      cancelLoad = undefined;
    }
    function fail(error: Error) {
      if (settled) return;
      settled = true;
      clean();
      document.getElementById(SDK_ID)?.remove();
      reject(error);
    }
    function findSelect() {
      const select = container.querySelector<HTMLSelectElement>(
        "select.goog-te-combo",
      );
      if (!select || settled) return;
      if (
        !SUPPORTED_LANGUAGES.filter((item) => item.code !== "en").every(
          (item) =>
            Array.from(select.options).some(
              (option) => option.value === item.code,
            ),
        )
      )
        return;
      settled = true;
      currentSelect = select;
      clean();
      resolve(select);
    }
    cancelLoad = () =>
      fail(new DOMException("Translation controller stopped", "AbortError"));
    function initialize() {
      if (initialized || settled) return;
      const TranslateElement = window.google?.translate?.TranslateElement;
      if (!TranslateElement) {
        fail(new Error("Google Translate is unavailable. Please retry."));
        return;
      }
      try {
        observer = new MutationObserver(findSelect);
        observer.observe(container, { childList: true, subtree: true });
        currentWidget = new TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: SUPPORTED_LANGUAGES.map(
              (item) => item.code,
            ).join(","),
            autoDisplay: false,
          },
          CONTAINER_ID,
        ) as TranslateWidget;
        initialized = true;
        findSelect();
      } catch {
        container.replaceChildren();
        fail(new Error("Google Translate could not start. Please retry."));
      }
    }

    if (window.google?.translate?.TranslateElement) initialize();
    else {
      window.planetwiseTranslateReady = initialize;
      document.getElementById(SDK_ID)?.remove();
      const script = document.createElement("script");
      script.id = SDK_ID;
      script.async = true;
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=planetwiseTranslateReady";
      script.onerror = () =>
        fail(
          new Error(
            "Google Translate could not load. Check your connection and retry.",
          ),
        );
      document.head.appendChild(script);
    }
  });
  // Rejections must release the failed attempt so a user can retry.
  widgetPromise = widgetPromise.finally(() => {
    widgetPromise = undefined;
  });
  return widgetPromise;
}

function waitForLanguage(
  language: Language,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(check);
    const contentObserver = new MutationObserver(check);
    function clean() {
      observer.disconnect();
      contentObserver.disconnect();
      clearTimeout(timer);
      clearTimeout(settleTimer);
      signal.removeEventListener("abort", abort);
    }
    function abort() {
      clean();
      reject(new DOMException("Language request superseded", "AbortError"));
    }
    function applied() {
      if (document.documentElement.lang !== language) return false;
      if (!translationProbe) return language === "en";
      const text = translationProbe.textContent?.trim();
      return language === "en"
        ? text === PROBE_SOURCE
        : Boolean(text && text !== PROBE_SOURCE);
    }
    function check() {
      if (!applied()) {
        clearTimeout(settleTimer);
        settleTimer = undefined;
      } else if (!settleTimer) {
        settleTimer = setTimeout(() => {
          settleTimer = undefined;
          // A superseded request can change the root during the quiet period.
          if (applied()) {
            clean();
            resolve();
          }
        }, 200);
      }
    }
    const timer = setTimeout(() => {
      clean();
      reject(
        new Error(
          "Google Translate could not apply this language. Please retry.",
        ),
      );
    }, TIMEOUT);
    signal.addEventListener("abort", abort, { once: true });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "class"],
    });
    if (translationProbe)
      contentObserver.observe(translationProbe, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    if (signal.aborted) abort();
    else check();
  });
}

export async function changeLanguage(
  value: string,
  persist = true,
): Promise<void> {
  if (!isLanguage(value)) throw new Error("Unsupported language");
  // A user/storage choice can recover one late SDK result. DOM mutations must
  // never grant themselves another retry and create a language-switch loop.
  canRecoverAppliedLanguage = true;
  await applyLanguage(value, persist);
}

async function applyLanguage(value: Language, persist: boolean): Promise<void> {
  const previous = getLanguageState();
  if (
    value === previous.language &&
    previous.status !== "error" &&
    previous.status !== "loading" &&
    document.documentElement.lang === value
  ) {
    if (persist) persistLanguage(value);
    updateLanguageState({
      requestedLanguage: value,
      status: "ready",
      error: null,
    });
    return;
  }
  activeRequest?.abort();
  const request = new AbortController();
  activeRequest = request;
  updateLanguageState({
    requestedLanguage: value,
    status: "loading",
    error: null,
  });
  try {
    const select = await ensureWidget();
    if (request.signal.aborted) return;
    // Start each new target from the same English source. This also resets a
    // partially failed Google request before retrying; changing html.lang alone
    // is not evidence that Google's translation service returned any content.
    if (
      currentWidget?.setEnabled &&
      (value === "en" || document.documentElement.lang !== "en")
    ) {
      const widget = currentWidget;
      const applied = waitForLanguage("en", request.signal);
      widget.setEnabled!(false); // Google's public restore operation.
      try {
        await applied;
        resetGoogleTextSources();
      } finally {
        widget.setEnabled!(true);
        // Leave Google's hidden controls ready for another inline selection.
        if (!request.signal.aborted) widget.showBanner?.(false);
      }
    }
    if (request.signal.aborted) return;
    if (value !== "en" || !currentWidget?.setEnabled) {
      if (
        value === "en" &&
        !Array.from(select.options).some((option) => option.value === "en")
      )
        select.add(new Option("English", "en"));
      if (!Array.from(select.options).some((option) => option.value === value))
        throw new Error(
          "Google Translate does not offer this language. Please retry.",
        );
      if (value !== "en") resetTranslationProbe();
      const applied = waitForLanguage(value, request.signal);
      select.value = value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      await applied;
    }
    if (request.signal.aborted) return;
    rememberCookie(value);
    if (persist) persistLanguage(value);
    updateLanguageState({
      language: value,
      requestedLanguage: value,
      status: "ready",
      error: null,
    });
  } catch (error) {
    if (request.signal.aborted) return;
    const message =
      error instanceof Error
        ? error.message
        : "Google Translate is unavailable. Please retry.";
    updateLanguageState({ status: "error", error: message });
    throw error;
  }
}

function onStorage(event: StorageEvent) {
  if (event.key === "locale" || event.key === null) {
    const next = isLanguage(event.newValue) ? event.newValue : "en";
    void changeLanguage(next, false).catch(() => {});
  }
}

/** One shared controller for the root, portals, header and both sidebars. */
export function startGoogleTranslation(): () => void {
  clients++;
  if (clients === 1) {
    window.addEventListener("storage", onStorage);
    appliedLanguageObserver = new MutationObserver(() => {
      const state = getLanguageState();
      if (
        state.status === "ready" &&
        currentSelect?.isConnected &&
        document.documentElement.lang !== state.language
      ) {
        // A superseded Google request can finish after a newer cross-tab choice.
        // Recover once, then surface a retry instead of repeatedly resetting
        // the entire page when the SDK keeps changing its language marker.
        if (!canRecoverAppliedLanguage) {
          updateLanguageState({
            status: "error",
            error:
              "Google Translate could not keep this language applied. Please retry.",
          });
          return;
        }
        canRecoverAppliedLanguage = false;
        void applyLanguage(state.requestedLanguage, false).catch(() => {});
      }
    });
    appliedLanguageObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"],
    });
    const preferred = readPreferredLanguage();
    if (preferred === "en") {
      clearTranslationCookies();
      updateLanguageState({
        language: "en",
        requestedLanguage: "en",
        status: "ready",
        error: null,
      });
    } else void changeLanguage(preferred).catch(() => {});
  }
  return () => {
    clients--;
    if (clients === 0) {
      window.removeEventListener("storage", onStorage);
      appliedLanguageObserver?.disconnect();
    }
  };
}

/** Release document-level resources when the application itself is disposed. */
export function disposeGoogleTranslation(): void {
  activeRequest?.abort();
  cancelLoad?.();
  stopBridge?.();
  stopBridge = undefined;
  currentSelect = undefined;
  currentWidget = undefined;
  widgetPromise = undefined;
  window.removeEventListener("storage", onStorage);
  appliedLanguageObserver?.disconnect();
  canRecoverAppliedLanguage = false;
  window.planetwiseTranslateReady = undefined;
  document.getElementById(CONTAINER_ID)?.remove();
  document.getElementById(SDK_ID)?.remove();
  translationProbe?.remove();
  translationProbe = undefined;
  clients = 0;
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    // Dispose the old engine before a development update installs another DOM
    // bridge; stacking prototype patches can freeze a translated page.
    try {
      currentWidget?.setEnabled?.(false);
    } finally {
      disposeGoogleTranslation();
    }
  });
}
